import { TodoItem, Project } from './adhd-todo-manager';
import { EnergyLevel } from './energy-context-service';
import { logger } from './logger';

export interface OverwhelmSignals {
  taskOverload: boolean;
  urgentTaskPileup: boolean;
  contextSwitchingStrain: boolean;
  energyMismatch: boolean;
  verbalOverwhelmCues: boolean;
  timeFragmentation: boolean;
}

export interface OverwhelmAssessment {
  overallLevel: 'none' | 'mild' | 'moderate' | 'severe';
  signals: OverwhelmSignals;
  confidence: number;
  primaryCauses: string[];
  recommendations: string[];
  immediateActions: string[];
}

export class OverwhelmDetector {
  private overwhelmKeywords = [
    'too much', 'overwhelmed', 'stressed', 'can\'t focus', 'scattered', 
    'everything is urgent', 'drowning', 'chaos', 'impossible', 'breaking down',
    'can\'t handle', 'losing it', 'falling behind', 'crushed', 'swamped',
    'burning out', 'exhausted', 'frazzled', 'spiraling', 'panicking'
  ];

  private urgentKeywords = [
    'urgent', 'emergency', 'asap', 'immediately', 'now', 'rush', 'critical',
    'deadline', 'overdue', 'behind', 'late', 'panic', 'crisis'
  ];

  /**
   * Analyze current state for overwhelm indicators
   */
  assessOverwhelm(
    todos: TodoItem[], 
    projects: Project[], 
    recentMessages: string[],
    currentEnergyLevel?: EnergyLevel,
    availableTimeMinutes?: number
  ): OverwhelmAssessment {
    const signals = this.detectOverwhelmSignals(
      todos, 
      projects, 
      recentMessages, 
      currentEnergyLevel, 
      availableTimeMinutes
    );

    const overallLevel = this.calculateOverwhelmLevel(signals);
    const confidence = this.calculateConfidence(signals, recentMessages.length);
    const primaryCauses = this.identifyPrimaryCauses(signals, todos);
    const recommendations = this.generateRecommendations(signals, overallLevel, todos);
    const immediateActions = this.generateImmediateActions(signals, overallLevel);

    logger.adhdAction('overwhelm_assessment', {
      overallLevel,
      signals: Object.entries(signals).filter(([, active]) => active).map(([signal]) => signal),
      confidence,
      todoCount: todos.length,
      urgentCount: todos.filter(t => t.priority === 'urgent').length
    });

    return {
      overallLevel,
      signals,
      confidence,
      primaryCauses,
      recommendations,
      immediateActions
    };
  }

  /**
   * Quick check if user is showing overwhelm signs
   */
  isOverwhelmed(
    todos: TodoItem[], 
    recentMessages: string[] = [],
    currentEnergyLevel?: EnergyLevel
  ): boolean {
    const assessment = this.assessOverwhelm(todos, [], recentMessages, currentEnergyLevel);
    return assessment.overallLevel === 'moderate' || assessment.overallLevel === 'severe';
  }

  /**
   * Check if message contains overwhelm cues
   */
  containsOverwhelmCues(message: string): boolean {
    const lowerMessage = message.toLowerCase();
    return this.overwhelmKeywords.some(keyword => lowerMessage.includes(keyword)) ||
           this.urgentKeywords.filter(keyword => lowerMessage.includes(keyword)).length >= 2;
  }

  /**
   * Get immediate overwhelm intervention
   */
  getOverwhelmIntervention(assessment: OverwhelmAssessment): {
    message: string;
    actions: Array<{ type: string; description: string; functionCall?: any }>;
  } {
    let message = '';
    const actions: Array<{ type: string; description: string; functionCall?: any }> = [];

    switch (assessment.overallLevel) {
      case 'severe':
        message = "I can see you're feeling really overwhelmed right now. Let's take a step back and create some calm. You're not alone in this.";
        actions.push(
          { 
            type: 'visual_simplify', 
            description: 'Simplify your view',
            functionCall: { name: 'collapse_all_projects' }
          },
          { 
            type: 'urgent_only', 
            description: 'Show only truly urgent tasks',
            functionCall: { name: 'get_tasks_by_quadrant', args: { quadrant: 1 } }
          },
          { 
            type: 'break_suggestion', 
            description: 'Take a 5-minute breathing break'
          }
        );
        break;

      case 'moderate':
        message = "It looks like things are feeling pretty intense right now. Let's organize this into manageable pieces.";
        actions.push(
          { 
            type: 'reprioritize', 
            description: 'Smart reorganization of all tasks',
            functionCall: { name: 'reprioritize_all_tasks' }
          },
          { 
            type: 'focus_suggestions', 
            description: 'Get personalized next steps',
            functionCall: { 
              name: 'get_focus_suggestions', 
              args: { 
                available_time_minutes: 30,
                current_energy_level: 'medium'
              }
            }
          }
        );
        break;

      case 'mild':
        message = "I notice some signs that things might be feeling scattered. Let me help you get organized.";
        actions.push(
          { 
            type: 'daily_focus', 
            description: 'Focus on today\'s tasks',
            functionCall: { name: 'get_todos', args: { filter_type: 'today' } }
          }
        );
        break;
    }

    return { message, actions };
  }

  private detectOverwhelmSignals(
    todos: TodoItem[], 
    projects: Project[], 
    recentMessages: string[],
    currentEnergyLevel?: EnergyLevel,
    availableTimeMinutes?: number
  ): OverwhelmSignals {
    const pendingTodos = todos.filter(t => t.status === 'pending');
    const urgentTodos = todos.filter(t => t.priority === 'urgent' && t.status === 'pending');
    const highPriorityTodos = todos.filter(t => 
      (t.priority === 'high' || t.priority === 'urgent') && t.status === 'pending'
    );

    // Task overload: too many pending tasks
    const taskOverload = pendingTodos.length > 15 || 
                        (pendingTodos.length > 8 && currentEnergyLevel === 'low');

    // Urgent task pileup: too many urgent tasks
    const urgentTaskPileup = urgentTodos.length >= 5 || 
                            (urgentTodos.length >= 3 && highPriorityTodos.length >= 7);

    // Context switching strain: tasks across too many projects
    const activeProjectCount = new Set(
      pendingTodos.map(t => t.project_id).filter(Boolean)
    ).size;
    const contextSwitchingStrain = activeProjectCount > 5 || 
                                  (activeProjectCount > 3 && currentEnergyLevel === 'low');

    // Energy mismatch: high energy tasks when energy is low
    const energyMismatch = currentEnergyLevel === 'low' && 
                          todos.some(t => t.adhd_energy_level === 'high' && t.status === 'pending');

    // Verbal overwhelm cues in recent messages
    const verbalOverwhelmCues = recentMessages.some(msg => 
      this.containsOverwhelmCues(msg)
    );

    // Time fragmentation: available time too small for pending tasks
    const timeFragmentation = availableTimeMinutes !== undefined &&
                             availableTimeMinutes < 30 &&
                             pendingTodos.filter(t => 
                               !t.estimated_minutes || t.estimated_minutes <= availableTimeMinutes
                             ).length < 3;

    return {
      taskOverload,
      urgentTaskPileup,
      contextSwitchingStrain,
      energyMismatch,
      verbalOverwhelmCues,
      timeFragmentation
    };
  }

  private calculateOverwhelmLevel(signals: OverwhelmSignals): 'none' | 'mild' | 'moderate' | 'severe' {
    const activeSignals = Object.values(signals).filter(Boolean).length;
    
    if (signals.verbalOverwhelmCues && activeSignals >= 3) return 'severe';
    if (signals.urgentTaskPileup && signals.taskOverload) return 'severe';
    if (activeSignals >= 4) return 'moderate';
    if (activeSignals >= 2) return 'mild';
    return 'none';
  }

  private calculateConfidence(signals: OverwhelmSignals, messageCount: number): number {
    let confidence = 50;
    
    if (signals.verbalOverwhelmCues) confidence += 30;
    if (signals.urgentTaskPileup) confidence += 20;
    if (signals.taskOverload) confidence += 15;
    if (messageCount >= 3) confidence += 10; // More context = higher confidence
    
    return Math.min(100, confidence);
  }

  private identifyPrimaryCauses(signals: OverwhelmSignals, todos: TodoItem[]): string[] {
    const causes: string[] = [];
    
    if (signals.taskOverload) {
      causes.push(`Too many pending tasks (${todos.filter(t => t.status === 'pending').length} active)`);
    }
    
    if (signals.urgentTaskPileup) {
      const urgentCount = todos.filter(t => t.priority === 'urgent' && t.status === 'pending').length;
      causes.push(`Urgent task buildup (${urgentCount} urgent tasks)`);
    }
    
    if (signals.contextSwitchingStrain) {
      causes.push('Too many active projects requiring attention switching');
    }
    
    if (signals.energyMismatch) {
      causes.push('Energy level doesn\'t match task requirements');
    }
    
    if (signals.verbalOverwhelmCues) {
      causes.push('Expressed feeling overwhelmed or stressed');
    }
    
    if (signals.timeFragmentation) {
      causes.push('Available time is too fragmented for meaningful progress');
    }
    
    return causes;
  }

  private generateRecommendations(
    signals: OverwhelmSignals, 
    level: string, 
    todos: TodoItem[]
  ): string[] {
    const recommendations: string[] = [];
    
    if (level === 'severe') {
      recommendations.push('Take a 5-10 minute break to reset your nervous system');
      recommendations.push('Focus only on the single most important task today');
      recommendations.push('Consider delegating or eliminating some tasks');
      recommendations.push('Use body doubling or accountability for difficult tasks');
    }
    
    if (signals.taskOverload) {
      recommendations.push('Review your task list and eliminate non-essential items');
      recommendations.push('Break large tasks into smaller, manageable steps');
      recommendations.push('Use the "2-minute rule" for quick wins');
    }
    
    if (signals.urgentTaskPileup) {
      recommendations.push('Distinguish between truly urgent and seemingly urgent tasks');
      recommendations.push('Block time for important but non-urgent work to prevent future urgency');
      recommendations.push('Consider if any "urgent" tasks can be handled by others');
    }
    
    if (signals.contextSwitchingStrain) {
      recommendations.push('Group similar tasks together to minimize context switching');
      recommendations.push('Dedicate specific time blocks to individual projects');
      recommendations.push('Consider putting some projects on hold temporarily');
    }
    
    if (signals.energyMismatch) {
      recommendations.push('Match task complexity to your current energy level');
      recommendations.push('Save complex tasks for high-energy periods');
      recommendations.push('Focus on admin and organization tasks during low energy');
    }
    
    return recommendations;
  }

  private generateImmediateActions(
    signals: OverwhelmSignals, 
    level: string
  ): string[] {
    const actions: string[] = [];
    
    if (level === 'severe' || level === 'moderate') {
      actions.push('Collapse all project views to reduce visual overwhelm');
      actions.push('Show only today\'s essential tasks');
      actions.push('Take 3 deep breaths before continuing');
    }
    
    if (signals.taskOverload) {
      actions.push('Run automatic task reprioritization');
      actions.push('Filter for high-priority tasks only');
    }
    
    if (signals.contextSwitchingStrain) {
      actions.push('Focus on tasks from a single project for now');
    }
    
    if (level === 'mild') {
      actions.push('Get personalized focus suggestions');
      actions.push('Review today\'s priorities');
    }
    
    return actions;
  }
}

// Create singleton instance
export const overwhelmDetector = new OverwhelmDetector();